/**
 * Cards — how cards move between accounts, and how each card range (BIN)
 * behaves. Data: GET /analytics/tt/shared-cards, /bins, /bin-series.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CreditCard, TriangleAlert, Users } from "lucide-react";
import { Segmented } from "../../../design/primitives";
import { useTTQuery } from "../ttApi";
import { bucketLabel, card, count, dateOnly, money, pct } from "../ttFormat";
import {
  AccountLinks,
  ChartBox,
  INK,
  LoadState,
  Note,
  OUTCOME_COLORS,
  Pager,
  Pill,
  ReviewCard,
  SearchBox,
  Select,
  TONE,
  Table,
  Tile,
  Tiles,
  Wrap,
  axisProps,
} from "../ttKit";

const PAGE_SIZE = 20;

const BIN_SORTS = [
  { value: "attempts", label: "Attempts" },
  { value: "fraud_declines", label: "Fraud declines" },
  { value: "fraud_rate", label: "Fraud rate" },
  { value: "blocked", label: "Blocked" },
];

export default function CardsPanel({ win, fresh, openAccount }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [win, q]);
  const shared = useTTQuery("shared-cards", { window: win, q, page, limit: PAGE_SIZE, fresh });

  return (
    <>
      <LoadState state={shared} height={120}>
        {(d) => (
          <Tiles>
            <Tile tone="amber" icon={CreditCard} label="Shared cards" value={count(d.kpis.shared_cards)} sub="Used by two or more accounts" />
            <Tile tone="blue" icon={Users} label="Accounts involved" value={count(d.kpis.accounts_involved)} sub="Used at least one shared card" />
            <Tile
              tone="red"
              icon={TriangleAlert}
              label="Shared cards refused for fraud"
              value={count(d.kpis.shared_cards_fraud)}
              sub={d.kpis.shared_cards ? `${pct(d.kpis.shared_cards_fraud / d.kpis.shared_cards)} of shared cards` : "—"}
            />
          </Tiles>
        )}
      </LoadState>

      <ReviewCard
        eyebrow="Card intelligence"
        title="Card ranges on shared cards"
        desc="First six digits of each shared card. A range that keeps coming back under different names points to one source of cards."
      >
        <LoadState state={shared} height={240}>
          {(d) =>
            d.bins.length ? (
              <ChartBox height={Math.max(160, d.bins.length * 26)}>
                <ResponsiveContainer>
                  <BarChart data={d.bins} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid horizontal={false} stroke={INK.line} />
                    <XAxis type="number" {...axisProps} allowDecimals={false} />
                    <YAxis type="category" dataKey="bin" {...axisProps} width={64} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="shared_cards" name="Shared cards" fill={TONE.blue.fg} />
                    <Bar dataKey="fraud_declined_cards" name="Of which refused for fraud" fill={OUTCOME_COLORS.fraud_declines} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartBox>
            ) : (
              <Note>No card was used by more than one account in this window.</Note>
            )
          }
        </LoadState>
      </ReviewCard>

      <ReviewCard
        eyebrow="Card intelligence"
        title="Cards used on more than one account"
        desc="Most accounts first, with the accounts in the order they used the card."
        tag="Shared card"
        tagTone="red"
        right={<SearchBox value={q} onChange={setQ} placeholder="Search card, BIN, account or name" />}
      >
        <LoadState state={shared}>
          {(d) => (
            <>
              <Table
                columns={SHARED_COLUMNS(openAccount)}
                rows={d.rows.map((r) => ({ key: r.card_key, ...r }))}
                emptyState={q ? "No shared card matches." : "No shared cards in this window."}
              />
              <Pager page={page} limit={PAGE_SIZE} total={d.total} onPage={setPage} />
            </>
          )}
        </LoadState>
      </ReviewCard>

      <BinSections win={win} fresh={fresh} />
    </>
  );
}

function BinSections({ win, fresh }) {
  const bins = useTTQuery("bins", { window: win, limit: 50, fresh });
  const [sort, setSort] = useState("attempts");
  const [selected, setSelected] = useState(null);
  const [bucket, setBucket] = useState("auto");

  const rows = useMemo(
    () => [...(bins.data?.bins || [])].sort((a, b) => (Number(b[sort]) || 0) - (Number(a[sort]) || 0)),
    [bins.data, sort]
  );
  const activeBin = selected && rows.some((r) => r.bin === selected) ? selected : rows[0]?.bin;
  const series = useTTQuery(
    "bin-series",
    { window: win, bin: activeBin, bucket: bucket === "auto" ? undefined : bucket, fresh },
    { enabled: !!activeBin }
  );
  const seriesBucket = series.data?.window?.bucket;

  return (
    <>
      <ReviewCard
        eyebrow="Card ranges"
        title="Card range ranking"
        desc="Top 50 ranges by attempts. Click a row to chart it below."
        right={<Segmented size="sm" options={BIN_SORTS} value={sort} onChange={setSort} />}
      >
        <LoadState state={bins}>
          {() => (
            <Table
              columns={BIN_COLUMNS}
              rows={rows.map((r) => ({ key: r.bin, ...r, selected: r.bin === activeBin, onClick: () => setSelected(r.bin) }))}
              emptyState="No attempts in this window."
            />
          )}
        </LoadState>
      </ReviewCard>

      <ReviewCard
        eyebrow="Card ranges"
        title={activeBin ? `Card range ${activeBin} over time` : "Card range trend"}
        right={
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            {rows.length > 0 && (
              <Select
                label="Range"
                value={activeBin || ""}
                onChange={setSelected}
                options={rows.map((r) => ({ value: r.bin, label: `${r.bin} (${count(r.attempts)})` }))}
              />
            )}
            <Segmented
              size="sm"
              value={bucket}
              onChange={setBucket}
              options={[
                { value: "auto", label: "Auto" },
                { value: "hour", label: "Hourly" },
                { value: "day", label: "Daily" },
              ]}
            />
          </div>
        }
      >
        {activeBin ? (
          <LoadState state={series} height={280}>
            {(d) => (
              <ChartBox height={300}>
                <ResponsiveContainer>
                  <LineChart
                    data={d.series.map((s) => ({ label: bucketLabel(s.bucket, seriesBucket), ...s }))}
                    margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid vertical={false} stroke={INK.line} />
                    <XAxis dataKey="label" {...axisProps} minTickGap={16} />
                    <YAxis {...axisProps} allowDecimals={false} width={40} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line dataKey="attempts" name="Attempts" stroke={OUTCOME_COLORS.attempts} strokeWidth={2} dot={false} />
                    <Line dataKey="completed" name="Completed" stroke={OUTCOME_COLORS.completed} strokeWidth={2} dot={false} />
                    <Line dataKey="fraud_declines" name="Fraud declines" stroke={OUTCOME_COLORS.fraud_declines} strokeWidth={2} dot={false} />
                    <Line dataKey="other_declines" name="Other declines" stroke={OUTCOME_COLORS.other_declines} strokeWidth={2} dot={false} />
                    <Line dataKey="blocked" name="Blocked by Idenza" stroke={OUTCOME_COLORS.blocked} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartBox>
            )}
          </LoadState>
        ) : (
          <Note>No card ranges in this window.</Note>
        )}
      </ReviewCard>
    </>
  );
}

const SHARED_COLUMNS = (openAccount) => [
  { key: "card", label: "Card", render: (r) => card(r.bin, r.last4, r.card_key) },
  { key: "accounts", label: "Accounts", numeric: true, render: (r) => count(r.accounts) },
  { key: "names", label: "Names", numeric: true, render: (r) => count(r.names) },
  {
    key: "fraud_declined",
    label: "Refused for fraud",
    render: (r) => <Pill tone={r.fraud_declined ? "red" : "slate"}>{r.fraud_declined ? "Yes" : "No"}</Pill>,
  },
  { key: "declines", label: "Total declines", numeric: true, render: (r) => count(r.declines) },
  { key: "completed_usd", label: "Completed", numeric: true, render: (r) => money(r.completed_usd) },
  { key: "passed_between", label: "Passed between", render: (r) => <AccountLinks emails={r.passed_between} onOpen={openAccount} /> },
  { key: "typed_as", label: "Typed as", render: (r) => <Wrap max={260}>{(r.typed_as || []).join(" · ") || "—"}</Wrap> },
  { key: "last_seen", label: "Last seen", render: (r) => dateOnly(r.last_seen) },
];

const BIN_COLUMNS = [
  { key: "bin", label: "Card range" },
  { key: "attempts", label: "Attempts", numeric: true, render: (r) => count(r.attempts) },
  { key: "cards", label: "Cards", numeric: true, render: (r) => count(r.cards) },
  { key: "accounts", label: "Accounts", numeric: true, render: (r) => count(r.accounts) },
  { key: "completed", label: "Completed", numeric: true, render: (r) => `${count(r.completed)} · ${money(r.completed_usd)}` },
  { key: "fraud_declines", label: "Fraud declines", numeric: true, render: (r) => count(r.fraud_declines) },
  { key: "other_declines", label: "Other declines", numeric: true, render: (r) => count(r.other_declines) },
  { key: "blocked", label: "Blocked", numeric: true, render: (r) => count(r.blocked) },
  { key: "fraud_rate", label: "Fraud rate", numeric: true, render: (r) => pct(r.fraud_rate) },
];
